import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import SendtooLogo from "@/components/shared/SendtooLogo";
import LegalLine from "@/components/shared/LegalLine";
import WhatsAppOptIn, { saveWhatsAppConsent } from "@/components/shared/WhatsAppOptIn";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [waOptIn, setWaOptIn] = useState(false); // unticked by default
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(form.name, form.email, form.phone, form.password);
      await saveWhatsAppConsent({ phone: form.phone, consent: waOptIn, source: "register", name: form.name, email: form.email });
      toast.success("Account created successfully!");
      navigate("/customer");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex font-body">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#0A183D] via-[#1B3FA0] to-[#0EA5E9] flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1659353741914-ee100869c2cf?w=800&q=80')", backgroundSize: "cover" }} />
        <div className="relative">
          <SendtooLogo size="2xl" />
        </div>
        <div className="relative">
          <h2 className="font-heading text-5xl font-bold text-white uppercase leading-tight mb-4">Join Sendtoo Today</h2>
          <p className="text-blue-200 text-lg mb-8">Create your free account and start shipping smarter.</p>
          <ul className="space-y-3 text-blue-100 text-sm">
            {["Free account setup", "Real-time shipment tracking", "Transparent pricing", "Instant wallet top-up", "24/7 support"].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#F97316]" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative text-xs text-blue-200">
          © {new Date().getFullYear()} Sendtoo Logistics
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden mb-8">
            <SendtooLogo size="lg" align="center" />
          </div>

          <h1 className="font-heading text-3xl font-bold text-slate-900 uppercase mb-1">Create Account</h1>
          <p className="text-slate-500 text-sm mb-8">Join thousands of businesses shipping with Sendtoo.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Full Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your full name"
                required
                data-testid="register-name"
                className="w-full px-4 py-3 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-slate-50 focus:bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Email Address</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@email.com"
                required
                data-testid="register-email"
                className="w-full px-4 py-3 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-slate-50 focus:bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Phone Number</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+91 9876543210"
                data-testid="register-phone"
                className="w-full px-4 py-3 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-slate-50 focus:bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Min. 6 characters"
                  required
                  data-testid="register-password"
                  className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 bg-slate-50 focus:bg-white"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Confirm Password</label>
              <input
                type="password"
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                placeholder="Repeat password"
                required
                data-testid="register-confirm-password"
                className="w-full px-4 py-3 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 bg-slate-50 focus:bg-white"
              />
            </div>

            <WhatsAppOptIn checked={waOptIn} onChange={setWaOptIn} testId="register-whatsapp-optin" />

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full py-3 bg-brand-500 text-white font-semibold rounded-sm hover:bg-brand-600 transition-colors disabled:opacity-70 flex items-center justify-center gap-2 uppercase tracking-wide"
            >
              <UserPlus className="w-4 h-4" />
              {loading ? "Creating account..." : "Create Account"}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Already have an account?{" "}
            <Link to="/auth/login" className="text-brand-500 font-semibold hover:underline">Sign in</Link>
          </p>
          <p className="text-center text-xs text-slate-400 mt-4">
            <Link to="/" className="hover:text-slate-600">← Back to home</Link>
          </p>
          <LegalLine className="mt-6" />
        </motion.div>
      </div>
    </div>
  );
}
